import ArticleLayout from './components/ArticleLayout';
import { articlesMeta } from '@/mocks/articlesData';

const data = articlesMeta.find((a) => a.slug === 'sugargoo-spreadsheet-guide')!;

export default function SugargooSpreadsheetGuide() {
  return (
    <ArticleLayout data={data}>
      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Introduction: Why a Sugargoo Spreadsheet Changes Everything
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Shopping internationally through agent platforms like Sugargoo opens up access to thousands of products that would otherwise be difficult or impossible to find. However, the challenge has always been the same: how do you <strong>efficiently discover</strong> the best products without spending countless hours scrolling through marketplace listings?
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          This is where a <strong>Sugargoo Spreadsheet</strong> becomes invaluable. Think of it as a curated, organized product map — a structured database that takes the chaos of endless product listings and transforms it into a clean, searchable, and actionable resource.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          In this complete guide, we will walk through everything you need to know about Sugargoo Spreadsheet resources: what they are, how they work, why they save you time and money, and how to use them effectively as part of your international shopping workflow.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          What Exactly Is a Sugargoo Spreadsheet?
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          A <strong>Sugargoo Spreadsheet</strong> is fundamentally a structured product discovery document — typically organized in spreadsheet or database format — that compiles detailed product information from various online marketplaces into one accessible location. Each entry in the spreadsheet typically contains:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            '<strong>Product Name &amp; Category:</strong> Clear identification of what the item is and which category it belongs to, making browsing intuitive.',
            '<strong>Product Images:</strong> Visual references so you can quickly assess style, color, and design before clicking through.',
            '<strong>Price Information:</strong> Reference pricing in local currency with notes on whether the price is considered budget, mid-range, or premium.',
            '<strong>Quality Notes:</strong> Community-sourced observations about material quality, sizing accuracy, and overall value.',
            '<strong>Direct Links:</strong> URLs that take you straight to the product listing on the marketplace, eliminating manual searching.',
            '<strong>Shopping Tips:</strong> Practical advice such as sizing recommendations, color options worth considering, or known issues to watch for.',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5 shrink-0"></i>
              <span className="text-sm text-foreground-700" dangerouslySetInnerHTML={{ __html: item }}></span>
            </li>
          ))}
        </ul>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          The beauty of this system is its simplicity. By centralizing product discovery into one structured format, a Sugargoo Spreadsheet eliminates the friction of jumping between different marketplace tabs, copying product codes, and manually comparing options.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          How to Use a Sugargoo Spreadsheet: Step-by-Step
        </h2>
        <div className="space-y-5">
          {[
            {
              title: 'Browse by Category',
              desc: 'Start by selecting a product category that interests you — shoes, hoodies, jackets, accessories, or any of the organized sections. The spreadsheet layout makes it easy to scan dozens of options at a glance.',
            },
            {
              title: 'Review Product Details',
              desc: 'Check the product image, price reference, and quality notes for items that catch your eye. Pay attention to community feedback about sizing and material quality — these insights can save you from disappointing purchases.',
            },
            {
              title: 'Open the Product Link',
              desc: 'Use the direct link to open the product listing on the marketplace. You can then copy the product URL or code into your Sugargoo agent account to begin the purchasing process.',
            },
            {
              title: 'Add to Your Agent Cart',
              desc: 'Paste the product link into Sugargoo and specify your size, color, and quantity preferences. The agent will handle the purchase on your behalf and ship the item to their warehouse.',
            },
            {
              title: 'Consolidate &amp; Ship',
              desc: 'Once multiple items arrive at the warehouse, consolidate them into one package to save on international shipping costs. Review QC photos before confirming shipment to ensure everything looks as expected.',
            },
          ].map((step, i) => (
            <div key={step.title} className="flex gap-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold shrink-0">
                {i + 1}
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground-800 mb-1">{step.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Top 5 Benefits of Using an Organized Sugargoo Spreadsheet
        </h2>
        <div className="space-y-4">
          {[
            {
              icon: 'ri-flashlight-line',
              color: 'text-primary-500',
              title: 'Massive Time Savings',
              desc: 'What typically takes hours of manual browsing across multiple marketplace tabs can be reduced to minutes. A well-organized Sugargoo Spreadsheet lets you scan hundreds of products in the time it would normally take to find just a few.',
            },
            {
              icon: 'ri-compass-3-line',
              color: 'text-accent-500',
              title: 'Discover Hidden Products',
              desc: 'Many of the best products are not on the first page of marketplace search results. Spreadsheet curators often find unique, high-quality items that would never surface through casual browsing.',
            },
            {
              icon: 'ri-scales-3-line',
              color: 'text-primary-500',
              title: 'Better Price Comparison',
              desc: 'With price references and quality notes side by side, you can easily compare similar products from different sellers and make informed decisions about which offers the best value.',
            },
            {
              icon: 'ri-shield-user-line',
              color: 'text-accent-500',
              title: 'Reduced Purchase Risk',
              desc: 'Community quality notes and sizing tips help you avoid common pitfalls. Knowing that a particular item runs small or that the material feels different from photos can save you from costly mistakes.',
            },
            {
              icon: 'ri-user-heart-line',
              color: 'text-primary-500',
              title: 'Beginner-Friendly Entry Point',
              desc: 'If you are new to using shopping agents like Sugargoo, a spreadsheet resource provides a gentle learning curve. Instead of figuring out marketplace navigation from scratch, you can follow curated recommendations.',
            },
          ].map((benefit) => (
            <div key={benefit.title} className="bg-background-100/60 rounded-xl p-4 md:p-5 border border-background-200/60">
              <h3 className="text-base font-semibold text-foreground-800 mb-1 flex items-center gap-2">
                <i className={`${benefit.icon} w-5 h-5 flex items-center justify-center ${benefit.color}`}></i>
                {benefit.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Understanding Sugargoo Shipping and the Spreadsheet Advantage
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          One of the most valuable aspects of using a Sugargoo Spreadsheet is how it integrates with your understanding of the <strong>Sugargoo shipping process</strong>. When you know the shipping costs and methods available, you can make smarter decisions about which products to order and how to bundle them.
        </p>
        <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/60 bg-background-100/50">
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Method</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Timeline</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Cost Level</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Best Use Case</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['DHL/FedEx', '3-7 days', 'Higher', 'Urgent orders, high-value items'],
                ['EMS', '7-15 days', 'Moderate', 'Balanced speed and value'],
                ['EUB/ePacket', '10-25 days', 'Lower', 'Budget-friendly small packages'],
                ['SAL', '15-30 days', 'Lowest', 'Maximum cost savings'],
              ].map((row) => (
                <tr key={row[0]} className="border-b border-background-200/40 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-foreground-800">{row[0]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[1]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[2]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          The spreadsheet approach shines here because you can plan your purchases around shipping economics. By consolidating multiple items from your Sugargoo Spreadsheet finds into a single package, you dramatically reduce the per-item shipping cost — sometimes by 50% or more compared to shipping items individually.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          The Sugargoo Warehouse: Your Quality Control Safety Net
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          A key feature that makes Sugargoo — and by extension, the Sugargoo Spreadsheet workflow — so reliable is the <strong>warehouse inspection process</strong>. When your items arrive at the Sugargoo warehouse, trained staff perform a quality control check and take detailed photos of each item.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          This QC process is crucial because it gives you a safety net. If an item arrives damaged, does not match the listing description, or has quality issues, you can identify the problem while the item is still in the warehouse. You can then request a return or exchange before the item is shipped internationally.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          When combined with community quality notes from a Sugargoo Spreadsheet, this warehouse inspection creates a powerful two-layer quality assurance system. The spreadsheet helps you avoid known problematic items, and the warehouse QC process catches any remaining issues before they reach your doorstep.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Tips for Getting the Most Out of Your Sugargoo Spreadsheet
        </h2>
        <div className="space-y-3">
          {[
            { title: 'Start with one category', desc: 'Do not try to browse everything at once. Pick one category — shoes, hoodies, or accessories — and explore it thoroughly before moving to the next.' },
            { title: 'Build a wishlist', desc: 'As you browse, note down items that interest you. This makes it easy to compare options later and plan your shipping consolidation strategy.' },
            { title: 'Check for updates regularly', desc: 'The best Sugargoo Spreadsheets are updated frequently with new finds. Check back regularly to discover fresh products and seasonal collections.' },
            { title: 'Combine with community research', desc: 'Use the spreadsheet finds as a starting point, then supplement with your own research on sizing, color accuracy, and seller reputation for the best results.' },
            { title: 'Plan shipments strategically', desc: 'Once you have a list of items you want, group them by similar shipping urgency. Ship time-sensitive items together with faster methods, and bundle non-urgent items for economy shipping.' },
          ].map((tip, i) => (
            <div key={tip.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 text-xs font-bold shrink-0">
                {i + 1}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-0.5">{tip.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Frequently Asked Questions About Sugargoo Spreadsheet
        </h2>
        <div className="space-y-4">
          {[
            {
              q: 'Are Sugargoo Spreadsheets free to use?',
              a: 'Many Sugargoo Spreadsheet resources are freely accessible as community-shared documents. Some premium spreadsheets may offer additional features like more frequent updates or exclusive finds, but the core concept is built around community sharing and accessibility.',
            },
            {
              q: 'How often are the spreadsheets updated?',
              a: 'Update frequency varies by spreadsheet curator. The best resources are updated weekly or even daily with new product discoveries, while others may be updated monthly. Look for spreadsheets that clearly show their last update date.',
            },
            {
              q: 'Can I request specific products to be added?',
              a: 'Some spreadsheet curators welcome product suggestions from the community. If you are looking for something specific that is not yet included, reaching out to the curator can sometimes result in your requested items being added in future updates.',
            },
            {
              q: 'Do I need a Sugargoo account to use the spreadsheet?',
              a: 'You can browse the spreadsheet without an account. However, to actually purchase any products you find, you will need a Sugargoo agent account to process orders and handle international shipping.',
            },
          ].map((faq) => (
            <div key={faq.q}>
              <h3 className="text-base font-semibold text-foreground-800 mb-1">{faq.q}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Conclusion: Your Next Steps
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          A <strong>Sugargoo Spreadsheet</strong> is one of the most efficient tools available for international shoppers who want to discover quality products without the friction of manual marketplace browsing. By centralizing product information, quality notes, and direct links into one organized resource, it transforms hours of searching into minutes of browsing.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Whether you are looking for the latest sneaker releases, streetwear essentials, accessories, or seasonal collections, the Sugargoo Spreadsheet approach gives you a significant advantage over traditional shopping methods. Combine it with smart shipping consolidation and warehouse QC inspection, and you have a complete system for safe, efficient, and cost-effective international shopping.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Ready to start exploring? Browse our organized product categories and discover what the Sugargoo Spreadsheet can do for your shopping experience.
        </p>
      </section>
    </ArticleLayout>
  );
}